// src/hooks/useAdhocMonitor.ts
// ─────────────────────────────────────────────────────────────────────────────
// Auth-free clone of useMonitor for kiosk sessions.
// ─────────────────────────────────────────────────────────────────────────────

import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import monitor, {
  AlertData,
  DeviceOrientation,
  EnabledVitalSigns,
  FaceSessionOptions,
  HealthMonitorCodes,
  HealthMonitorSession,
  ImageValidity,
  OfflineMeasurements,
  SessionState,
  UserInformation,
  VitalSigns,
  VitalSignsResults,
} from '@biosensesignal/web-sdk';
import { InfoType, InfoData, ReportVitalSigns } from '../types';
import { adhocLog } from '../utils/adhocLogger';

// ── Re-use the same vital-sign mapper from the main hook ─────────────────────

const vitalNode = <T,>(
  node: { value?: T; confidenceLevel?: number } | null | undefined,
  isEnabled: boolean | undefined,
  hasValue: boolean,
) => ({
  value: node?.value,
  isEnabled: isEnabled ?? hasValue,
  confidenceLevel: node?.confidenceLevel ?? null,
});

const mapToReportVitalSigns = (
  vitalSigns: VitalSigns | null,
  enabledVitalSigns?: EnabledVitalSigns,
): ReportVitalSigns => ({
  pulseRate: vitalNode(vitalSigns?.pulseRate, enabledVitalSigns?.isEnabledPulseRate, vitalSigns?.pulseRate?.value != null),
  respirationRate: vitalNode(vitalSigns?.respirationRate, enabledVitalSigns?.isEnabledRespirationRate, vitalSigns?.respirationRate?.value != null),
  stress: vitalNode(vitalSigns?.stressLevel, enabledVitalSigns?.isEnabledStressLevel, vitalSigns?.stressLevel?.value != null),
  hrvSdnn: vitalNode(vitalSigns?.sdnn, enabledVitalSigns?.isEnabledSdnn, vitalSigns?.sdnn?.value != null),
  // spo2 disabled — not available from web SDK
  spo2: { value: undefined, isEnabled: false, confidenceLevel: null },
  // spo2: vitalNode(vitalSigns?.oxygenSaturation, enabledVitalSigns?.isEnabledSpo2, vitalSigns?.oxygenSaturation?.value != null),
  bloodPressure: vitalNode(vitalSigns?.bloodPressure, enabledVitalSigns?.isEnabledBloodPressure, vitalSigns?.bloodPressure?.value?.systolic != null && vitalSigns?.bloodPressure?.value?.diastolic != null),
  hemoglobin: vitalNode(vitalSigns?.hemoglobin, enabledVitalSigns?.isEnabledHemoglobin, vitalSigns?.hemoglobin?.value != null),
  hemoglobinA1c: vitalNode(vitalSigns?.hemoglobinA1c, enabledVitalSigns?.isEnabledHemoglobinA1c, vitalSigns?.hemoglobinA1c?.value != null),
  wellnessIndex: vitalNode(vitalSigns?.wellnessIndex, enabledVitalSigns?.isEnabledWellnessIndex, vitalSigns?.wellnessIndex?.value != null),
});

const CAMERA_ERROR_CODES = new Set([1001, 1002, 1005]);
const CAMERA_RETRY_DELAY_MS = 1000;

// ── Hook interface ────────────────────────────────────────────────────────────

interface useAdhocMonitorOptions {
  video: MutableRefObject<HTMLVideoElement>;
  cameraId: string | undefined;
  processingTime: number;
  licenseKey: string;
  startMeasuring: boolean;
  /** When false, SDK init is deferred (e.g. on landing before payment). */
  shouldInitSdk?: boolean;
  shouldInitCamera?: boolean;
  scanInitKey?: number;
  userInformation?: UserInformation;
  /** Called with raw VitalSignsResults and license flags when scan completes. */
  onScanComplete: (results: VitalSignsResults, enabledVitalSigns?: EnabledVitalSigns) => void;
}

const useAdhocMonitor = ({
  video,
  cameraId,
  processingTime,
  licenseKey,
  startMeasuring,
  shouldInitSdk = true,
  shouldInitCamera = true,
  scanInitKey = 0,
  userInformation,
  onScanComplete,
}: useAdhocMonitorOptions) => {
  const [session, setSession] = useState<HealthMonitorSession>();
  const [sessionState, setSessionState] = useState<SessionState>();
  const [isMonitorReady, setIsMonitorReady] = useState<boolean>(false);
  const [enabledVitalSigns, setEnabledVitalSigns] = useState<EnabledVitalSigns>();
  const [offlineMeasurements, setOfflineMeasurements] = useState<OfflineMeasurements>();
  const [vitalSigns, setVitalSigns] = useState<VitalSigns | null>();
  const [finalReport, setFinalReport] = useState<ReportVitalSigns>();
  const [rawResults, setRawResults] = useState<any>();
  const [reportGeneratedAt, setReportGeneratedAt] = useState<string>();
  const [error, setError] = useState<AlertData>({ code: -1 });
  const [warning, setWarning] = useState<AlertData>({ code: -1 });
  const [info, setInfo] = useState<InfoData>({ type: InfoType.NONE });
  
  const [sessionKey, setSessionKey] = useState(0);
  const autoRetriedRef = useRef(false);
  const monitorInitializedRef = useRef(false);
  const isDismissing = useRef<boolean>(false);
  const lastImageLogRef = useRef(0);

  const onScanCompleteRef = useRef(onScanComplete);
  useEffect(() => { onScanCompleteRef.current = onScanComplete; }, [onScanComplete]);

  const setInfoWithDismiss = useCallback((newInfo: InfoData, seconds?: number) => {
    if (!isDismissing.current) {
      setInfo(newInfo);
      if (seconds) {
        isDismissing.current = true;
        setTimeout(() => {
          setInfo({ type: InfoType.NONE });
          isDismissing.current = false;
        }, seconds * 1000);
      }
    }
  }, []);

  const updateVitalSigns = useCallback((vs: VitalSigns) => {
    setVitalSigns((prev) => ({ ...prev, ...vs }));
  }, []);

  const onVitalSign = useCallback((vs: VitalSigns) => updateVitalSigns(vs), [updateVitalSigns]);

  const onFinalResults = useCallback((vitalSignsResults: VitalSignsResults) => {
    adhocLog('scan', 'info', 'Scan final results received');
    const vitals: VitalSigns = (vitalSignsResults as any)?.results ?? vitalSignsResults as any;

    setRawResults(vitals);
    updateVitalSigns(vitals);
    setFinalReport(mapToReportVitalSigns(vitals, enabledVitalSigns));
    setReportGeneratedAt(new Date().toISOString());
    setVitalSigns(null);

    onScanCompleteRef.current(vitalSignsResults, enabledVitalSigns);
  }, [enabledVitalSigns, updateVitalSigns]);

  const onError = useCallback((errorData: AlertData) => {
    adhocLog('scan', 'error', 'SDK error', { code: errorData.code });
    setError(errorData);
  }, []);

  const onWarning = (warningData: AlertData) => {
    adhocLog('scan', 'warn', 'SDK warning', { code: warningData.code });
    if (warningData.code === HealthMonitorCodes.MEASUREMENT_CODE_MISDETECTION_DURATION_EXCEEDS_LIMIT_WARNING) {
      setVitalSigns(null);
    }
    setWarning(warningData);
  };

  const onStateChange = useCallback((state: SessionState) => {
    adhocLog('scan', 'info', 'SDK session state change', { state: String(state) });
    setSessionState(state);
    if (state === SessionState.MEASURING) {
      setVitalSigns(null);
      setRawResults(undefined);
    }
  }, []);

  const onEnabledVitalSigns = useCallback((vs: EnabledVitalSigns) => setEnabledVitalSigns(vs), []);
  const onOfflineMeasurement = useCallback((om: OfflineMeasurements) => setOfflineMeasurements(om), []);
  const onActivation = useCallback((_id: string) => {}, []);

  const onImageData = useCallback((imageValidity: ImageValidity) => {
    if (imageValidity !== ImageValidity.VALID) {
      const now = Date.now();
      if (now - lastImageLogRef.current >= 5000) {
        lastImageLogRef.current = now;
        adhocLog('scan', 'debug', 'Face image invalid', { imageValidity: String(imageValidity) });
      }
      const messages: Partial<Record<ImageValidity, string>> = {
        [ImageValidity.INVALID_DEVICE_ORIENTATION]: 'Unsupported Orientation',
        [ImageValidity.TILTED_HEAD]: 'Head Tilted',
        [ImageValidity.FACE_TOO_FAR]: 'You Are Too Far',
        [ImageValidity.UNEVEN_LIGHT]: 'Uneven Lighting',
      };
      setInfo({ type: InfoType.INSTRUCTION, message: messages[imageValidity] ?? 'Face Not Detected' });
    } else {
      setInfoWithDismiss({ type: InfoType.NONE });
    }
  }, [setInfoWithDismiss]);

  // ── 1. Initialize monitor ────────────────────────────────────────────────
  useEffect(() => {
    if (!shouldInitSdk || !licenseKey) {
      return undefined;
    }

    if (monitorInitializedRef.current) {
      setIsMonitorReady(true);
      return undefined;
    }

    let active = true;
    adhocLog('scan', 'info', 'Initializing BioSense SDK');

    (async () => {
      try {
        await monitor.initialize({
          licenseKey,
          licenseInfo: {
            onEnabledVitalSigns,
            onOfflineMeasurement,
            onActivation,
          },
        });

        if (!active) return;
        monitorInitializedRef.current = true;
        adhocLog('scan', 'info', 'BioSense SDK initialized');
        setIsMonitorReady(true);
        setError({ code: -1 });
      } catch (e: any) {
        if (!active) return;
        adhocLog('scan', 'error', 'BioSense SDK init failed', { code: e.errorCode ?? 7006 });
        setIsMonitorReady(false);
        setError({ code: e.errorCode ?? 7006 });
      }
    })();

    return () => { active = false; };
  }, [licenseKey, shouldInitSdk]); // eslint-disable-line

  // ── 2. Create face session ────────────────────────────────────────────────
  
useEffect(() => {
  if (!shouldInitCamera) {
    setSession((prev) => {
      if (prev) {
        adhocLog('scan', 'info', 'Camera disabled — terminating session');
        prev.terminate();
      }
      return undefined;
    });
    setSessionState(undefined);
    return;
  }

  if (!isMonitorReady || !processingTime || !video.current || !cameraId) {
    adhocLog('scan', 'debug', 'Face session deferred', {
      shouldInitCamera,
      isMonitorReady,
      processingTime,
      hasVideo: !!video.current,
      hasCameraId: !!cameraId,
    });
    return;
  }

  // Use a ref instead of a closure variable so the cleanup from a previous
  // render cannot poison the in-flight async call of the new render.
  const cancelledRef = { cancelled: false };

  (async () => {
    try {
      adhocLog('scan', 'info', 'Creating face session', { cameraId });
      const options: FaceSessionOptions = {
        input: video.current!,
        cameraDeviceId: cameraId,
        processingTime,
        onVitalSign,
        onFinalResults,
        onError,
        onWarning,
        onStateChange,
        orientation: DeviceOrientation.PORTRAIT,
        onImageData,
        ...(userInformation ? { userInformation } : {}),
      };

      const faceSession = await monitor.createFaceSession(options);

      if (cancelledRef.cancelled) {
        adhocLog('scan', 'debug', 'Face session created after cleanup — terminating');
        faceSession.terminate();
        return;
      }

      adhocLog('scan', 'info', 'Face session created');
      setSession(faceSession);
      setError({ code: -1 });
      autoRetriedRef.current = false;
    } catch (e: any) {
      if (cancelledRef.cancelled) return;
      const errorCode = e.errorCode;
      adhocLog('scan', 'error', 'Face session creation failed', { code: errorCode });

      if (CAMERA_ERROR_CODES.has(errorCode) && !autoRetriedRef.current) {
        autoRetriedRef.current = true;
        adhocLog('scan', 'warn', 'Retrying face session after camera error', { code: errorCode });
        window.setTimeout(() => setSessionKey((k) => k + 1), CAMERA_RETRY_DELAY_MS);
        return;
      }
      setError({ code: errorCode });
    }
  })();

  return () => {
    cancelledRef.cancelled = true;
    setSession((prev) => {
      if (prev) {
        adhocLog('scan', 'debug', 'Face session cleanup — terminating');
        prev.terminate();
      }
      return undefined;
    });
  };
}, [shouldInitCamera, isMonitorReady, processingTime, cameraId, sessionKey, scanInitKey]); // eslint-disable-line
  // ── 3. Cleanup on unmount ──────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      setSession((prev) => {
        if (prev) {
          adhocLog('scan', 'debug', 'Unmount — terminating session');
          prev.terminate();
        }
        return undefined;
      });
    };
  }, []);

  // ── 4. Start / stop measuring ──────────────────────────────────────────────
  useEffect(() => {
    if (startMeasuring) {
      if (sessionState === SessionState.ACTIVE) {
        adhocLog('scan', 'info', 'Measurement started');
        setFinalReport(undefined);
        setReportGeneratedAt(undefined);
        session?.start();
        setError({ code: -1 });
      } else if (
        sessionState !== SessionState.MEASURING
        && sessionState !== SessionState.STOPPING
      ) {
        adhocLog('scan', 'debug', 'Waiting to start measurement', {
          sessionState: sessionState != null ? String(sessionState) : 'undefined',
        });
      }
    } else if (sessionState === SessionState.MEASURING) {
      adhocLog('scan', 'info', 'Measurement stopped');
      session?.stop();
    }
  }, [startMeasuring, session, sessionState]);

  const retrySession = useCallback(() => {
    adhocLog('scan', 'info', 'Manual scan retry requested');
    autoRetriedRef.current = false;
    setError({ code: -1 });
    setSessionKey((k) => k + 1);
  }, []);

  const clearScanAlert = useCallback(() => {
    setError({ code: -1 });
    setWarning({ code: -1 });
  }, []);

  return {
    sessionState,
    isMonitorReady,
    vitalSigns: mapToReportVitalSigns(vitalSigns ?? null, enabledVitalSigns),
    finalReport,
    rawResults,
    reportGeneratedAt,
    offlineMeasurements,
    error,
    warning,
    info,
    clearScanAlert,
    retrySession,
  };
};

export default useAdhocMonitor;
